import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Package, TrendingUp, Shield, Ship, Activity, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODULES = [
  { id: 'dashboard', name: 'Dashboard', icon: TrendingUp, description: 'KPIs, charts, vessel map' },
  { id: 'trade_analytics', name: 'Trade Analytics', icon: Package, description: 'Reports, opportunities, alerts' },
  { id: 'insurance', name: 'Freight Insurance', icon: Shield, description: 'Quotes, risk assessment, policies' },
  { id: 'marketplace', name: 'Sagisu Marketplace', icon: Ship, description: 'RFQs, matching, deal rooms' },
  { id: 'integrations', name: 'Integrations', icon: LinkIcon, description: 'API connections' }
];

const PERMISSION_LEVELS = [
  { value: 'no_access', label: 'No Access', color: 'text-[hsl(var(--muted-foreground))]' },
  { value: 'view', label: 'View Only', color: 'text-[hsl(var(--info))]' },
  { value: 'edit', label: 'Edit', color: 'text-[hsl(var(--warning))]' },
  { value: 'full_control', label: 'Full Control', color: 'text-[hsl(var(--success))]' }
];

export default function AdminModulesPage() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/modules`, {
        withCredentials: true
      });
      setModules(response.data);
    } catch (error) {
      console.error('[AdminModules] Error fetching:', error);
      toast.error('Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModule = async (moduleId, enabled) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/modules/${moduleId}`,
        { enabled },
        { withCredentials: true }
      );
      toast.success(`Module ${enabled ? 'enabled' : 'disabled'}`);
      await fetchModules();
    } catch (error) {
      console.error('[AdminModules] Error toggling:', error);
      toast.error('Failed to toggle module');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Package className="h-12 w-12 animate-pulse text-[hsl(var(--muted-foreground))]" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="admin-modules-page">
      <div>
        <h1 className="text-3xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
          Module Control
        </h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-1">
          Enable/disable modules and set global permissions
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {MODULES.map((module) => {
          const moduleData = modules.find(m => m.id === module.id) || { enabled: true };
          const Icon = module.icon;
          
          return (
            <Card key={module.id} data-testid={`module-${module.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[hsl(var(--accent))]/10">
                      <Icon className="h-5 w-5 text-[hsl(var(--accent))]" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{module.name}</CardTitle>
                      <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                        {module.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={moduleData.enabled}
                    onCheckedChange={(checked) => handleToggleModule(module.id, checked)}
                    data-testid={`toggle-${module.id}`}
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[hsl(var(--muted-foreground))]">Status:</span>
                  <Badge variant={moduleData.enabled ? 'default' : 'outline'}>
                    {moduleData.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[hsl(var(--muted-foreground))]">Default Permission:</span>
                  <Badge variant="outline">
                    {moduleData.default_permission || 'View'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Permission Matrix Info */}
      <Card>
        <CardHeader>
          <CardTitle>Permission Levels</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PERMISSION_LEVELS.map(level => (
              <div key={level.value} className="space-y-2">
                <Badge variant="outline" className={level.color}>{level.label}</Badge>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  {level.value === 'no_access' && 'Module completely hidden from user'}
                  {level.value === 'view' && 'Can view data, no modifications'}
                  {level.value === 'edit' && 'Can view and modify existing records'}
                  {level.value === 'full_control' && 'Full access: create, edit, delete'}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
